import { differenceInDays, format, formatDistanceToNow, parseISO } from 'date-fns';

import { SECONDS_PER_HOUR, SECONDS_PER_MINUTE } from './unitsOfTime';

/**
 * @description This is the `date-fns` format for year-month-day. This particular format (that includes dashes) should be avoided if possible as it
 * could break or render the incorrect date when not within the date picker. Prefer slashes (if you absolutely need this format). This format yields: `2021-06-28`.
 * @url `date-fns` format options: https://date-fns.org/v2.22.1/docs/format
 */
export const YEAR_MONTH_DAY_FORMAT = 'yyyy-MM-dd';

export const MONTH_DAY_YEAR_FORMAT = 'MMM d, yyyy';

export const MONTH_DAY_TIME_FORMAT = 'MMM dd hh:mma';

export const MONTH_DAY_YEAR_TIME_FORMAT = "MMM dd, yyyy 'at' h:mma";

// Why doesn't JS have stringf? Just the worst really
const prependZero = (val: number, override = true) => (override && val < 10 ? `0${val}` : val);

/**
 * Takes a number of milliseconds (like the current or remaining seconds in a video clip) and returns a pretty timestamp
 * @example
 * // return '7:02'
 * formatDuration(422)
 * @returns {string} A pretty timestamp like '7:02' or '1:12:10'
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.trunc(seconds / SECONDS_PER_HOUR);
  const minutes = Math.trunc((seconds / SECONDS_PER_MINUTE) % SECONDS_PER_MINUTE);
  const hoursString = hours ? `${hours}:` : ``;

  return `${hoursString}${prependZero(minutes, !!hours)}:${prependZero(Math.trunc(seconds % SECONDS_PER_MINUTE))}`;
};

/**
 * Formats a whole-second duration as compact UI copy (`7s`, `2m 53s`, `1m`).
 */
export const formatCompactDurationSeconds = (seconds: number): string => {
  if (typeof seconds !== 'number') {
    throw new TypeError('formatCompactDurationSeconds expects a number of seconds');
  }

  if (!Number.isFinite(seconds) || seconds < 0) {
    throw new RangeError('formatCompactDurationSeconds expects a non-negative finite number of seconds');
  }

  const wholeSeconds = Math.trunc(seconds);

  if (wholeSeconds < SECONDS_PER_MINUTE) {
    return `${wholeSeconds}s`;
  }

  const minutes = Math.floor(wholeSeconds / SECONDS_PER_MINUTE);
  const remainingSeconds = wholeSeconds % SECONDS_PER_MINUTE;

  return remainingSeconds === 0 ? `${minutes}m` : `${minutes}m ${remainingSeconds}s`;
};

/**
 * Takes a number of seconds and returns a rounded, human-readable duration string
 * @example
 * // returns '45 sec'
 * formatRoundedDuration(45)
 * @example
 * // returns '1 min'
 * formatRoundedDuration(75)
 * @example
 * // returns '2 min'
 * formatRoundedDuration(110)
 * @returns {string} A human-readable duration like '45 sec' or '2 min'
 */
export const formatRoundedDuration = (seconds: number): string => {
  const totalMinutes = seconds / 60;

  // Under 1 min: Show X sec
  if (totalMinutes < 1) {
    return `${Math.round(seconds)} sec`;
  }

  // For durations 1:00 and above, round to the nearest minute based on rules
  // 1:00-1:29 → Show 1 min
  // 1:30-2:29 → Show 2 min
  // 2:30-3:29 → Show 3 min, etc.
  // biome-ignore lint/style/noMagicNumbers: round-half-up midpoint
  const roundedMinutes = Math.floor(totalMinutes + 0.5);

  return `${roundedMinutes} min`;
};

export const parseAirDateToISO = (date: string) => parseISO(date);

export const getIsoDateWithoutTime = (date: Date) => date.toISOString().split('T')[0];

export const formatDateVerbose = (date: string) => format(parseAirDateToISO(date), MONTH_DAY_YEAR_FORMAT);

type RelativeLocale = {
  lessThanXSeconds: string;
  xSeconds: string;
  halfAMinute: string;
  lessThanXMinutes: string;
  xMinutes: string;
  aboutXHours: string;
  xHours: string;
  xDays: string;
  aboutXWeeks: string;
  xWeeks: string;
  aboutXMonths: string;
  xMonths: string;
  aboutXYears: string;
  xYears: string;
  overXYears: string;
  almostXYears: string;
};

const FORMAT_UPDATED_AT_RELATIVE_LOCALE: RelativeLocale = {
  aboutXHours: '{{count}}h',
  aboutXMonths: '{{count}}mo',
  aboutXWeeks: '{{count}}w',
  aboutXYears: '{{count}}yr',
  almostXYears: '{{count}}yr',
  halfAMinute: 'just now',
  lessThanXMinutes: 'just now',
  lessThanXSeconds: 'just now',
  overXYears: '{{count}}yr',
  xDays: '{{count}}d',
  xHours: '{{count}}h',
  xMinutes: '{{count}}m',
  xMonths: '{{count}}mo',
  xSeconds: 'just now',
  xWeeks: '{{count}}w',
  xYears: '{{count}}yr',
};

const FORMAT_UPDATED_AT_LOCALE_OPTIONS = {
  locale: {
    formatDistance: (token: keyof RelativeLocale, count: number) =>
      FORMAT_UPDATED_AT_RELATIVE_LOCALE[token].replace('{{count}}', `${count}`),
  },
};

type FormatUpdatedAtOptions = {
  addSuffix?: boolean;
  thresholdDays?: number;
};

export const formatUpdatedAt = (
  date: Parameters<typeof formatDistanceToNow>[0],
  { addSuffix = false, thresholdDays = 2 }: FormatUpdatedAtOptions = {},
) => {
  if (Math.abs(differenceInDays(date, new Date())) >= thresholdDays) {
    return format(date, 'M/d/yyyy');
  }

  const result = formatDistanceToNow(date, FORMAT_UPDATED_AT_LOCALE_OPTIONS);

  if (!addSuffix || result === 'just now') {
    return result;
  }

  return `${result} ago`;
};
